/**
 * Critical CSS for 2024 styling
 * This file contains the minimal CSS needed to prevent
 * layout shifts when 2024 styling loads.
 */

// Critical font faces - adjust paths to be absolute for inlining in head
export const criticalFontFaces = `
  @font-face {
    font-family: 'Graphik';
    font-weight: 400;
    font-display: fallback;
    src: url('/fonts/Graphik-Regular.woff2') format('woff2');
    font-style: normal;
  }
  @font-face {
    font-family: 'Graphik';
    font-weight: 500;
    font-display: fallback;
    src: url('/fonts/Graphik-Medium.woff2') format('woff2');
    font-style: normal;
  }
  @font-face {
    font-family: 'Graphik';
    font-weight: 600;
    font-display: swap;
    src: url('/fonts/Graphik-Semibold.woff2') format('woff2');
    font-style: normal;
  }
  @font-face {
    font-family: 'Graphik';
    font-weight: 400;
    font-style: italic;
    font-display: swap;
    src: url('/fonts/Graphik-RegularItalic.woff2') format('woff2');
  }
  @font-face {
    font-family: 'Boing';
    font-weight: 500;
    font-display: fallback;
    src: url('/fonts/Boing-Medium.woff2') format('woff2');
    font-style: normal;
  }
  @font-face {
    font-family: 'Boing';
    font-weight: 400;
    font-display: swap;
    src: url('/fonts/Boing-Regular.woff2') format('woff2');
    font-style: normal;
  }
  @font-face {
    font-family: 'More Sugar';
    font-weight: 400;
    font-display: swap;
    src: url('/fonts/More Sugar.woff2') format('woff2');
  }
  @font-face {
    font-family: 'More Sugar';
    font-weight: 200;
    font-display: swap;
    src: url('/fonts/More Sugar Thin.woff2') format('woff2');
  }
`;

// Critical CSS
export const criticalCss = `
  body {
    margin: 0;
    font-family: 'Graphik', 'Helvetica Neue', Helvetica, Arial, sans-serif;
    line-height: 1.5;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    color: var(--stile-grey-8);
  }

  /* Push main content below the fixed header. This padding must live on
     Gatsby's focus wrapper — NOT on <body> — because the router focuses this
     element on every client-side navigation and the browser scrolls it into
     view: with the offset on <body> the wrapper starts below y=0 and every
     navigation lands scrolled down by the header height. The 2024 Header
     keeps this value in sync with the real header height at runtime. */
  #gatsby-focus-wrapper {
    padding-top: 80px;
  }

  .content {
    max-width: 1160px;
    margin: 0 auto;
  }

  :root {
    /* Colors */
    --stile-green-1: #00e65a;
    --stile-green-2: #00cb50;
    --stile-green-3: #00a541;
    --stile-green-hover: #07fa66;
    --white: #ffffff;
    --stile-grey-1: #f7f5f3;
    --stile-grey-2: #ece9e5;
    --stile-grey-3: #dad4cc;
    --stile-grey-4: #b5b0a9;
    --stile-grey-5: #918c87;
    --stile-grey-6: #706d69;
    --stile-grey-7: #4f4d4a;
    --stile-grey-8: #2b2b2b;

    /* Spacing variables to prevent layout shifts */
    --space-s: clamp(1rem, calc(0.91rem + 0.45vw), 1.31rem);
    --space-m: clamp(1.5rem, calc(1.36rem + 0.71vw), 2rem);
    --space-l: clamp(2rem, calc(1.82rem + 0.89vw), 2.63rem);
  }

  * {
    box-sizing: border-box;
  }

  /* Typography styles matching typography.ts */
  h1 {
    font-family: 'Boing', 'Helvetica Neue', Helvetica, Arial, sans-serif;
    font-size: 60px;
    font-weight: 500;
    line-height: 65px;
  }
  h2 {
    font-family: 'Boing', 'Helvetica Neue', Helvetica, Arial, sans-serif;
    font-size: 55px;
    font-weight: 500;
    line-height: 60px;
  }
  h3 {
    font-family: 'Boing', 'Helvetica Neue', Helvetica, Arial, sans-serif;
    font-size: 50px;
    font-weight: 500;
    line-height: 55px;
  }
  h4 {
    font-family: 'Boing', 'Helvetica Neue', Helvetica, Arial, sans-serif;
    font-size: 40px;
    font-weight: 500;
    line-height: 45px;
  }
  h5 {
    font-family: 'Graphik', 'Helvetica Neue', Helvetica, Arial, sans-serif;
    font-size: 30px;
    font-weight: 600;
    line-height: 38px;
  }
  h6 {
    font-family: 'Graphik', 'Helvetica Neue', Helvetica, Arial, sans-serif;
    font-size: 21px;
    font-weight: 600;
    line-height: 26px;
  }
  h6.h7 {
    font-family: 'Graphik', 'Helvetica Neue', Helvetica, Arial, sans-serif;
    font-size: 16px;
    font-weight: 600;
    line-height: 24px;
  }
  h6.h8 {
    font-family: 'Graphik', 'Helvetica Neue', Helvetica, Arial, sans-serif;
    font-size: 14px;
    font-weight: 600;
    line-height: 21px;
  }
  h6.h9 {
    font-family: 'Graphik', 'Helvetica Neue', Helvetica, Arial, sans-serif;
    font-size: 18px;
    font-weight: 500;
    line-height: 15px;
    text-transform: uppercase;
  }

  .--huge {
    font-family: 'Boing', 'Helvetica Neue', Helvetica, Arial, sans-serif;
    font-size: 120px;
    font-weight: 500;
    line-height: 105px;
    @media (max-width: 1700px) {
      font-size: 96px;
      line-height: 88px;
    }
    @media (max-width: 1024px) {
      font-size: 88px;
      line-height: 80px;
    }
    @media (max-width: 768px) {
      font-size: 62px;
      line-height: 60px;
    }
  }
  .--jumbo {
    font-family: 'Boing', 'Helvetica Neue', Helvetica, Arial, sans-serif;
    font-size: 74px;
    font-weight: 500;
    line-height: 73.958px;
    @media (max-width: 1024px) {
      font-size: 64px;
      line-height: 64px;
    }
    @media (max-width: 768px) {
      font-size: 50px;
      line-height: 50px;
    }
  }

  .feature {
    font-family: 'Graphik', 'Helvetica Neue', Helvetica, Arial, sans-serif;
    font-size: 30px;
    font-weight: 400;
    line-height: 39px;
  }

  .quote {
    font-family: 'Graphik', 'Helvetica Neue', Helvetica, Arial, sans-serif;
    font-size: 16px;
    font-style: italic;
    font-weight: 400;
    line-height: 24px;
  }
  .quote.--small {
    font-family: 'Graphik', 'Helvetica Neue', Helvetica, Arial, sans-serif;
    font-size: 14px;
    font-weight: 400;
    line-height: 21px;
  }

  @media (max-width: 768px) {
    h1 {
      font-size: 38px;
      line-height: 39px;
    }
    h2 {
      font-size: 34px;
      line-height: 35px;
    }
    h3 {
      font-size: 32px;
      line-height: 35px;
    }
    h4 {
      font-size: 26px;
      line-height: 28px;
    }
    h5 {
      font-size: 22px;
      line-height: 26px;
    }
    h6 {
      font-size: 19px;
      line-height: 24px;
    }
    h6.h7 {
      font-size: 16px;
      line-height: 24px;
    }
    h6.h8 {
      line-height: 18px;
    }
    h6.h9 {
      font-size: 14px;
    }
  }

  @media (max-width: 1220px) {
    .content {
      margin: 0 60px;
    }
  }

  @media (max-width: 768px) {
    .content {
      margin: 0 25px;
    }
  }

  .js-loading img:not(.skeleton) {
    opacity: 0;
    transition: opacity 0.3s ease-in-out;
  }

  .js-loading-complete img {
    opacity: 1;
  }

  img {
    max-width: 100%;
    height: auto;
  }
`;

// Skeleton styles remain unchanged
export const skeletonStyles = `
  @keyframes skeleton-loading {
    0% {
      background-color: rgba(227, 227, 227, 0.6);
    }
    100% {
      background-color: rgba(245, 245, 245, 0.8);
    }
  }
  .skeleton {
    display: inline-block;
    position: relative;
    overflow: hidden;
    background-color: rgba(227, 227, 227, 0.6);
    border-radius: 4px;
  }
  .skeleton::after {
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    transform: translateX(-100%);
    background: linear-gradient(
      90deg,
      rgba(255, 255, 255, 0) 0%,
      rgba(255, 255, 255, 0.2) 20%,
      rgba(255, 255, 255, 0.5) 60%,
      rgba(255, 255, 255, 0)
    );
    animation: skeleton-shine 1.5s infinite;
    content: '';
  }
  @keyframes skeleton-shine {
    100% {
      transform: translateX(100%);
    }
  }
  .skeleton-text {
    width: 100%;
    height: 1em;
    margin-bottom: 0.5em;
  }
  .skeleton-image {
    width: 100%;
    height: auto;
    aspect-ratio: 16/9;
    border-radius: 8px;
  }
`;

// Combine all critical CSS — font faces + layout/typography rules only.
// Skeleton styles are NOT critical and load with styled-components instead.
export const criticalStyles = `
  ${criticalFontFaces}
  ${criticalCss}
`;
